/**
 * The agency's fee income, defined once.
 *
 * Until Sep 2026 five screens each kept their own idea of it: Reports
 * counted a generic "income" type the others did not; the Desk widget
 * counted only commission payments; Goals summed ledger rows by insert
 * time with payouts included. Two pages could show two numbers for the
 * same month. There are exactly two honest figures, and every screen now
 * names which one it shows:
 *
 *   FEES COLLECTED   cash that arrived: transactions of the fee types
 *                    below, on their transaction date.
 *   FEES BOOKED      the commission ledger's commission_earned rows, on
 *                    their transaction date. Shown to people as "fees
 *                    booked", not "fees earned", because the two are not the
 *                    same thing: reconcileContractCommission writes a row
 *                    from earned money OR from the guarantee, whichever is
 *                    larger, and early in a contract that is the guarantee.
 *                    Every contract row on the demo book was rate x
 *                    guaranteed_value while the screens called it earned.
 *                    The ledger is still the figure, because it is the only
 *                    one that also holds commission with no contract behind
 *                    it, which a walk over contracts cannot see.
 *
 * The function below is still called feesEarned: it reads rows whose
 * transaction_type is commission_earned, which is a stored value and not
 * ours to rename. The displayed name is the one that had to be true.
 *
 * Read this figure from here. Do not re-derive it per screen: that is the
 * split this file was written to end, and it reopened once already - the
 * desk read the ledger and showed $13.4M while Revenue & fees priced its own
 * and showed $2.4M for the same book, both labelled "Fees earned".
 *
 * Paper value (rate × total contract value) is neither, and is shown only
 * when labelled as such.
 */

import type { Transaction } from '../types'
import type { CommissionLedgerEntry } from '../types/commission'
import { parseDateOnly, startOfLocalDay } from './formatters'

// Transaction types that are fee income: one list, in shared/ so the Claude connector reads it too.
import { FEE_INCOME_TYPES } from '../shared/feeTypes'
export { FEE_INCOME_TYPES }

export interface FeeWindow {
  /** Inclusive local calendar day, 'YYYY-MM-DD' or Date. */
  from?: string | Date | null
  /** Inclusive local calendar day. */
  to?: string | Date | null
}

function inWindow(dateValue: string | null | undefined, window: FeeWindow | undefined): boolean {
  if (!window || (!window.from && !window.to)) return true
  const when = parseDateOnly(dateValue)
  if (!when) return false
  const t = startOfLocalDay(when).getTime()
  if (window.from) {
    const f = parseDateOnly(window.from)
    if (f && t < startOfLocalDay(f).getTime()) return false
  }
  if (window.to) {
    const l = parseDateOnly(window.to)
    if (l && t > startOfLocalDay(l).getTime()) return false
  }
  return true
}

export function isFeeIncome(t: Pick<Transaction, 'type'>): boolean {
  return FEE_INCOME_TYPES.has(String(t.type))
}

/** Fees collected: cash fee income, optionally for one client and one window. Null when nothing qualifies. */
export function feesCollected(
  transactions: Pick<Transaction, 'type' | 'amount' | 'transaction_date' | 'client_id'>[],
  opts: FeeWindow & { clientId?: string } = {},
): { total: number | null; count: number } {
  const rows = transactions.filter(t =>
    isFeeIncome(t)
    && (!opts.clientId || t.client_id === opts.clientId)
    && inWindow(t.transaction_date, opts),
  )
  if (rows.length === 0) return { total: null, count: 0 }
  return { total: rows.reduce((s, t) => s + (Number(t.amount) || 0), 0), count: rows.length }
}

/** This calendar year so far, as a window. */
export function yearToDate(now: Date = new Date()): FeeWindow {
  return { from: new Date(now.getFullYear(), 0, 1), to: now }
}

/** Fees earned: the ledger's commission_earned rows, optionally for one agent and one window. */
export function feesEarned(
  ledger: Pick<CommissionLedgerEntry, 'transaction_type' | 'gross_amount' | 'net_amount' | 'agent_share' | 'agency_share' | 'transaction_date' | 'agent_id'>[],
  opts: FeeWindow & { agentId?: string } = {},
): { gross: number | null; net: number | null; agentShare: number | null; agencyShare: number | null; count: number } {
  const rows = ledger.filter(e =>
    e.transaction_type === 'commission_earned'
    && (!opts.agentId || e.agent_id === opts.agentId)
    && inWindow(e.transaction_date, opts),
  )
  if (rows.length === 0) return { gross: null, net: null, agentShare: null, agencyShare: null, count: 0 }
  const sum = (k: 'gross_amount' | 'net_amount' | 'agent_share' | 'agency_share') => rows.reduce((s, e) => s + (Number(e[k]) || 0), 0)
  return { gross: sum('gross_amount'), net: sum('net_amount'), agentShare: sum('agent_share'), agencyShare: sum('agency_share'), count: rows.length }
}
